import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';

function PDFPreview() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState('');
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTemplate();
    loadFiles();
  }, [templateId]);

  useEffect(() => {
    if (selectedFile) {
      loadRecords();
    }
  }, [selectedFile]);

  const loadTemplate = async () => {
    try {
      const response = await api.get(`/templates/${templateId}`);
      setTemplate(response.data);
    } catch (err) {
      setError('Failed to load template');
      console.error(err);
    }
  };

  const loadFiles = async () => {
    try {
      const response = await api.get('/files');
      setFiles(response.data);
    } catch (err) {
      setError('Failed to load files');
      console.error(err);
    }
  };

  const loadRecords = async () => {
    try {
      const response = await api.get(`/records/${selectedFile}?limit=50`);
      setRecords(response.data.records);
      setSelectedRecord('');
      setPdfUrl('');
    } catch (err) {
      setError('Failed to load records');
      console.error(err);
    }
  };

  const generatePreview = async () => {
    if (!selectedRecord) {
      setError('Please select a record');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      const response = await api.post('/preview-pdf', {
        record_id: parseInt(selectedRecord),
        template_id: parseInt(templateId)
      }, {
        responseType: 'blob'
      });

      // Create blob URL for PDF display
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);

    } catch (err) {
      setError('Failed to generate preview');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPreview = () => {
    if (!pdfUrl) return;

    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `preview_${selectedRecord}_${template?.name || 'template'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderRecordData = (record) => {
    return (
      <div className="bg-gray-50 p-3 rounded text-xs">
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(record.data).slice(0, 6).map(([key, value]) => (
            <div key={key}>
              <span className="font-medium text-gray-600">{key}:</span>{' '}
              <span className="text-gray-900">{value || 'N/A'}</span>
            </div>
          ))}
        </div>
        {Object.keys(record.data).length > 6 && (
          <div className="mt-2 text-gray-500">
            ... and {Object.keys(record.data).length - 6} more fields
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          PDF Preview: {template?.name}
        </h1>
        <p className="text-gray-600 mt-2">
          Select a data file and record to preview the PDF output
        </p>
      </div>

      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Controls */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Preview Settings</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Data File
                </label>
                <select
                  value={selectedFile}
                  onChange={(e) => setSelectedFile(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose a file...</option>
                  {files.map(file => (
                    <option key={file.id} value={file.id}>
                      {file.file_name} ({file.total_rows} rows)
                    </option>
                  ))}
                </select>
              </div>

              {selectedFile && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Record
                  </label>
                  <select
                    value={selectedRecord}
                    onChange={(e) => setSelectedRecord(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose a record...</option>
                    {records.map((record, index) => (
                      <option key={record.id} value={record.id}>
                        Record #{index + 1} (ID: {record.id})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <button
                onClick={generatePreview}
                disabled={!selectedRecord || isLoading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Generating...' : 'Generate Preview'}
              </button>

              {pdfUrl && (
                <button
                  onClick={downloadPreview}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  Download PDF
                </button>
              )}
            </div>
          </div>

          {/* Record Data Preview */}
          {selectedRecord && records.find(r => r.id.toString() === selectedRecord) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Record Data</h3>
              {renderRecordData(records.find(r => r.id.toString() === selectedRecord))}
            </div>
          )}

          {/* Template Info */}
          {template && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Template Info</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium text-gray-600">Paper Size:</span>{' '}
                  <span className="text-gray-900">{template.paper_size}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Created:</span>{' '}
                  <span className="text-gray-900">{new Date(template.created_at).toLocaleDateString()}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Fields:</span>{' '}
                  <span className="text-gray-900">
                    {JSON.parse(template.layout_json).fields?.length || 0}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* PDF Preview */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">PDF Preview</h3>
              <div className="space-x-2">
                <button
                  onClick={() => navigate(`/templates/${templateId}`)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Edit Template
                </button>
                <button
                  onClick={() => navigate('/templates')}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Back to Templates
                </button>
              </div>
            </div>
            
            {pdfUrl ? (
              <div className="border border-gray-300 rounded-lg overflow-hidden" style={{ height: '80vh' }}>
                <iframe
                  src={pdfUrl}
                  width="100%"
                  height="100%"
                  title="PDF Preview"
                  className="border-0"
                />
              </div>
            ) : (
              <div className="border border-gray-300 rounded-lg flex items-center justify-center bg-gray-50" style={{ height: '80vh' }}>
                <div className="text-center">
                  <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Preview Available</h3>
                  <p className="text-gray-500">
                    Select a data file and record, then click "Generate Preview" to see the PDF output.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PDFPreview;
